import {useState} from 'react'
import {CircleUser as UserCircle, Mail, Shield} from 'lucide-react'
import {useApp} from '../../context/AppContext'
import {getRoleConfig} from '../../utils/helpers'
import type {User} from '../../types'
import Avatar from '../common/Avatar'
import Badge from '../common/Badge'
import UserDetailModal from './UserDetailModal'
import PageHeader from '../common/PageHeader'

export default function UserList() {
  const {state, getTeamById} = useApp()
  const [selectedUser, setSelectedUser] = useState<User | null>(null)

  return (
    <div className='devops-page'>
      <PageHeader
        eyebrow='Pessoas e acesso'
        icon={UserCircle}
        title='Pessoas'
        description='Visualize responsabilidades, papéis e carga de trabalho dos profissionais envolvidos nas entregas.'
        meta={
          <>
            <span>{state.users.length} membros</span>
            <span>{state.users.filter((user) => user.isOnline).length} online agora</span>
          </>
        }
      />

      <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'>
        {state.users.length === 0 && (
          <div className='devops-empty-state col-span-full'>
            <div>
              <UserCircle size={28} className='mx-auto' />
              <p className='text-sm font-medium text-slate-600'>Nenhuma pessoa encontrada</p>
              <p className='mt-1 text-xs'>Os membros com acesso ao workspace aparecerão aqui.</p>
            </div>
          </div>
        )}
        {state.users.map((user, index) => {
          const role = getRoleConfig(user.role) //user.role
          const userTeams = user.teamIds.map((id) => getTeamById(id)).filter(Boolean)
          const userTasks = state.tasks.filter((t) => t.assigneeIds.includes(user.id))
          const activeTasks = userTasks.filter(
            (t) => t.status === 'in_progress' || t.status === 'in_review'
          ).length

          return (
            <div
              key={user.id}
              onClick={() => setSelectedUser(user)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  setSelectedUser(user)
                }
              }}
              className='devops-card p-5 cursor-pointer animate-slide-in-up'
              style={{animationDelay: `${index * 60}ms`}}
              role='button'
              tabIndex={0}
              aria-label={`Abrir perfil de ${user.name}`}
            >
              <div className='flex items-start gap-3 mb-4'>
                <Avatar
                  name={user.name}
                  color={user.avatarColor}
                  size='xl'
                  isOnline={user.isOnline}
                />
                <div className='flex-1 min-w-0'>
                  <h3 className='text-base font-semibold text-slate-800 truncate'>{user.name}</h3>
                  <div className='flex items-center gap-1.5 text-xs text-slate-400 mt-0.5'>
                    <Mail size={12} />
                    <span className='truncate'>{user.email}</span>
                  </div>
                </div>
              </div>

              <div className='flex items-center gap-2 mb-3'>
                <div className='flex items-center gap-1'>
                  <Shield size={12} className='text-slate-400' />
                  <Badge className={role?.color}>{role?.label}</Badge>
                </div>
              </div>

              <div className='flex flex-wrap gap-1.5 mb-3'>
                {userTeams.map((team) => (
                  <span
                    key={team!.id}
                    className='inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium border'
                    style={{
                      borderColor: `${team!.color}40`,
                      backgroundColor: `${team!.color}10`,
                      color: team!.color,
                    }}
                  >
                    {team!.name}
                  </span>
                ))}
              </div>

              <div className='flex items-center justify-between pt-3 border-t border-slate-100 text-xs'>
                <span className='text-slate-500'>{userTasks.length} tarefas totais</span>
                <span className='text-brand-600 font-medium'>{activeTasks} ativas</span>
              </div>
            </div>
          )
        })}
      </div>

      {selectedUser && (
        <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}
    </div>
  )
}
